/**
 * KPI Dashboard Page
 * Main dashboard for franchise partners to view their KPIs
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { GroupedKpiOverviewCard } from '@/components/dashboard/GroupedKpiOverviewCard';
import { KpiPeriodDropdownsFixed as KpiPeriodDropdowns } from '@/components/dashboard/KpiPeriodDropdownsFixed';
import { UserMenu } from '@/components/UserMenu';
import { MainNavigation } from '@/components/MainNavigation';
import { AlertCircle, BarChart3, TrendingUp, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

const KpiDashboard: React.FC = () => {
  const { sellerId: authSellerId, franchiseName, loading: authLoading } = useAuth();
  
  const [periodDates, setPeriodDates] = useState({
    start: '',
    end: ''
  });
  
  const [periodLabel, setPeriodLabel] = useState('');
  const [sellerId, setSellerId] = useState('');
  const [isDemo, setIsDemo] = useState(false);

  // Update sellerId when auth context loads
  useEffect(() => {
    if (!authLoading) {
      if (authSellerId) {
        setSellerId(authSellerId);
        setIsDemo(false);
      } else {
        // No seller mapping - use demo mode
        setSellerId('6811cd6fd12f18cbd45175b9'); // Demo seller - Marco Heer test data
        setIsDemo(true);
      }
    }
  }, [authSellerId, authLoading]);

  // Handle period selection from dropdowns
  const handlePeriodSelect = (start: string, end: string, label: string) => {
    setPeriodDates({ start, end });
    setPeriodLabel(label);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          <p className="mt-2 text-gray-600 dark:text-gray-400">Lade Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex flex-col">
      {/* Header */}
      <header className="bg-primary-700 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-white">XORA KPI Dashboard</h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <MainNavigation />
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                KPI Übersicht {franchiseName ? `- ${franchiseName}` : ''}
              </h2>
            </div>
          </div>

          {/* Demo Mode Alert */}
          {isDemo && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 text-amber-800 p-6 mb-8 rounded-xl shadow-sm flex items-start">
              <div className="flex-shrink-0 mr-4 mt-1">
                <AlertCircle className="h-6 w-6 text-amber-600" />
              </div>
              <div className="flex-1 space-y-2">
                <p className="font-semibold text-lg">Demo-Modus aktiv</p>
                <p className="text-amber-700 leading-relaxed">
                  Keine Seller-Zuordnung für Ihr Konto gefunden. Es werden Demo-Daten angezeigt. 
                  Kontaktieren Sie den Support, um Ihr Franchise-Konto zu verknüpfen.
                </p>
              </div>
            </div>
          )}

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Period Selector - Left Sidebar */}
            <div className="lg:col-span-1">
              <KpiPeriodDropdowns
                onPeriodSelect={handlePeriodSelect}
              />
              
              {/* Quick Stats */}
              <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg p-4 shadow-lg">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
                  <h3 className="font-semibold">Zeitraum Details</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Auswahl:</span>
                    <span className="font-medium">{periodLabel}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Von:</span>
                    <span className="font-medium">{periodDates.start && new Date(periodDates.start).toLocaleDateString('de-DE')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Bis:</span>
                    <span className="font-medium">{periodDates.end && new Date(periodDates.end).toLocaleDateString('de-DE')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* KPI Cards - Main Content */}
            <div className="lg:col-span-3">
              {sellerId && (
                <GroupedKpiOverviewCard
                  sellerId={sellerId}
                  periodStart={periodDates.start}
                  periodEnd={periodDates.end}
                  className="h-full"
                />
              )}
            </div>
          </div>

          {/* KPI Update Info - dezent am Ende */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              <Info className="inline h-4 w-4 mr-1" />
              Die KPIs werden jeweils am 2. Tag des Monats für den Vormonat aktualisiert
            </p>
          </div>

        </div>
      </main>
    </div>
  );
};

export default KpiDashboard; 